import { useSelector, useDispatch } from "react-redux";
import { fetchClinicianProfileAnalytics } from "../../redux/clinic/clinicSlice";
import { useCallback } from "react";
import { RootState } from '../../redux';
import { AppDispatch } from '../../redux';

const useClinicianAnalytics = () => {
  const dispatch = useDispatch<AppDispatch>();
  const analyticsState = useSelector((state: RootState) => state.clinics.analytics);
  
  // Helper function to convert date range to Unix timestamps
  const getDateRangeTimestamps = (dateRange: string): { timeStart: number; timeEnd: number } => {
    const now = new Date();
    let end = new Date(now);
    let start = new Date(now);

    switch (dateRange) {
      case 'Today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'Yesterday':
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(now.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case 'Last 7 days':
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case 'Last 30 days':
        start.setDate(now.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        break;
      case 'Last 90 days':
        start.setDate(now.getDate() - 90);
        start.setHours(0, 0, 0, 0);
        break;
      case 'This month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'Last month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      default:
        // Default to last 30 days
        start.setDate(now.getDate() - 30);
        start.setHours(0, 0, 0, 0);
    }

    return {
      timeStart: Math.floor(start.getTime() / 1000), // Convert to Unix timestamp (seconds)
      timeEnd: Math.floor(end.getTime() / 1000)
    };
  };

  const fetchAnalytics = useCallback(async (practiceId?: string, dateRange: string = 'Last 30 days') => {
    if (!practiceId) {
      console.error('Practice ID is required');
      return;
    }

    
    const { timeStart, timeEnd } = getDateRangeTimestamps(dateRange);
    
    try {
      const result = await dispatch(fetchClinicianProfileAnalytics({ 
        practiceId, 
        timeStart, 
        timeEnd 
      })).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      throw error;
    }
  }, [dispatch]);
  
  return { 
    ...analyticsState, 
    fetchAnalytics
  };
};

export default useClinicianAnalytics;
